import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Mail, Inbox, Trash2, Send, FileText, RotateCcw, X, Eye, Save, AlertTriangle, Loader2 } from "lucide-react";
import { correosApi, apiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const TA = (props) => (
  <textarea {...props} className={`w-full rounded-xl border border-black/10 bg-white px-3.5 py-2.5 text-sm text-[#0A1C30] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]/40 ${props.className || ""}`} />
);

const STATUS_BADGE = {
  sent: { t: "Enviado", c: "bg-emerald-100 text-emerald-700" },
  failed: { t: "Falló", c: "bg-red-100 text-red-700" },
  skipped: { t: "No enviado", c: "bg-amber-100 text-amber-700" },
  deleted: { t: "Eliminado", c: "bg-gray-200 text-gray-600" },
};

const TABS = [
  { id: "sent", label: "Enviados", icon: Send },
  { id: "received", label: "Recibidos", icon: Inbox },
  { id: "deleted", label: "Eliminados", icon: Trash2 },
  { id: "templates", label: "Plantillas", icon: FileText },
];

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
}

export default function CorreosAdmin() {
  const [tab, setTab] = useState("sent");
  const [emails, setEmails] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [test, setTest] = useState({ to: "", subject: "Correo de prueba · Supera", body: "<p>Hola, este es un correo de prueba desde el panel Supera.</p>" });
  const [sending, setSending] = useState(false);
  const [tpls, setTpls] = useState({});
  const [meta, setMeta] = useState({ email_configured: false, sender: "" });
  const [editKey, setEditKey] = useState(null);
  const [editData, setEditData] = useState({ subject: "", body: "" });

  const loadBox = useCallback(async (box) => {
    setLoading(true);
    try {
      const d = await correosApi.list(box);
      setEmails(d.emails || []);
      setTotal(d.total || 0);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const d = await correosApi.templates();
      setTpls(d.templates || {});
      setMeta({ email_configured: d.email_configured, sender: d.sender });
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => {
    if (tab === "templates") loadTemplates();
    else loadBox(tab);
    setPreview(null);
  }, [tab, loadBox, loadTemplates]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const openPreview = async (id) => {
    try { setPreview(await correosApi.get(id)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const del = async (id) => { await correosApi.remove(id); toast.success("Correo movido a Eliminados"); loadBox(tab); if (preview?.id === id) setPreview(null); };
  const restore = async (id) => { await correosApi.restore(id); toast.success("Correo restaurado"); loadBox(tab); if (preview?.id === id) setPreview(null); };
  const purge = async (id) => { if (!window.confirm("¿Eliminar este correo definitivamente? No se puede deshacer.")) return; await correosApi.purge(id); toast.success("Eliminado definitivamente"); loadBox(tab); if (preview?.id === id) setPreview(null); };
  const emptyTrash = async () => { if (!window.confirm("¿Vaciar la papelera? Se borrarán definitivamente todos los correos eliminados.")) return; const r = await correosApi.emptyTrash(); toast.success(`Papelera vaciada (${r.deleted})`); loadBox("deleted"); };

  const sendTest = async () => {
    if (!test.to.trim()) return toast.error("Indica un correo destino.");
    setSending(true);
    try {
      const r = await correosApi.test(test);
      if (r.status === "sent") toast.success("Correo de prueba enviado ✅");
      else if (r.status === "skipped") toast.warning("Resend no está configurado; correo omitido.");
      else toast.error("Falló el envío: " + (r.error || ""));
      if (tab === "sent") loadBox("sent");
    } catch (e) { toast.error(apiError(e)); }
    setSending(false);
  };

  const startEdit = (key) => { const t = tpls[key] || {}; setEditKey(key); setEditData({ subject: t.subject || "", body: t.body || "" }); };
  const saveTemplate = async () => {
    try {
      const d = await correosApi.saveTemplate(editKey, editData);
      setTpls(d.templates || {});
      setEditKey(null);
      toast.success("Plantilla guardada");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="correos-admin" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#0A1C30] text-gold flex items-center justify-center"><Mail className="w-5 h-5" /></div>
        <div>
          <h1 className="text-2xl font-extrabold text-[#0A1C30]">Correos</h1>
          <p className="text-sm text-[#6B7688]">Gestión de correos del sistema y plantillas editables.</p>
        </div>
      </div>

      {/* Estado de configuración */}
      <div className={`rounded-xl px-4 py-3 text-sm font-semibold flex items-center gap-2 ${meta.email_configured ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="correos-status">
        {meta.email_configured ? <Send className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
        {meta.email_configured ? `Envío activo · Remitente: ${meta.sender}` : "Resend no configurado — los correos no se enviarán."}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-black/5">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`correos-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-t-xl transition-colors ${tab === t.id ? "bg-[#0A1C30] text-white" : "text-[#6B7688] hover:bg-black/5"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Plantillas */}
      {tab === "templates" && (
        <div className="space-y-3" data-testid="correos-templates">
          {Object.keys(tpls).length === 0 && <p className="text-sm text-[#6B7688]">Cargando plantillas…</p>}
          {Object.entries(tpls).map(([key, t]) => (
            <div key={key} className="rounded-2xl border border-black/10 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-bold text-[var(--gold)] uppercase tracking-wide">{t.label || key}</div>
                  <div className="text-sm font-extrabold text-[#0A1C30] mt-0.5">{t.subject}</div>
                </div>
                {editKey !== key && (
                  <button data-testid={`correos-edit-${key}`} onClick={() => startEdit(key)} className="text-xs font-bold px-3 py-1.5 rounded-lg bg-black/5 hover:bg-black/10 text-[#0A1C30]">Editar</button>
                )}
              </div>
              {editKey === key ? (
                <div className="mt-3 space-y-3">
                  <div>
                    <Label className="text-xs">Asunto</Label>
                    <Input data-testid={`correos-subject-${key}`} value={editData.subject} onChange={(e) => setEditData({ ...editData, subject: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Contenido (HTML)</Label>
                    <TA data-testid={`correos-body-${key}`} rows={6} value={editData.body} onChange={(e) => setEditData({ ...editData, body: e.target.value })} />
                    <p className="text-[11px] text-[#6B7688] mt-1">Variables disponibles: {"{nombre}"}, {"{programa}"}, {"{alumno}"}, {"{code}"}, {"{mensaje}"} (según la plantilla).</p>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`correos-save-${key}`} onClick={saveTemplate} className="flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl bg-[var(--gold)] text-[#0A1C30]"><Save className="w-4 h-4" /> Guardar</button>
                    <button onClick={() => setEditKey(null)} className="flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl bg-black/5 text-[#0A1C30]"><X className="w-4 h-4" /> Cancelar</button>
                  </div>
                  <div className="rounded-xl border border-black/5 overflow-hidden">
                    <div className="text-[11px] font-bold px-3 py-1.5 bg-black/5 text-[#6B7688]">Vista previa</div>
                    <iframe title="preview" srcDoc={editData.body} className="w-full h-40 bg-white" />
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-xs text-[#6B7688] line-clamp-2" dangerouslySetInnerHTML={{ __html: t.body || "" }} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recibidos placeholder */}
      {tab === "received" && (
        <div className="rounded-2xl border border-dashed border-black/15 bg-white p-10 text-center" data-testid="correos-received-empty">
          <Inbox className="w-10 h-10 text-[#6B7688] mx-auto mb-3" />
          <p className="text-sm font-bold text-[#0A1C30]">Bandeja de recibidos vacía</p>
          <p className="text-xs text-[#6B7688] mt-1 max-w-md mx-auto">La recepción de correos entrantes requiere activar Inbound en Resend. Por ahora esta bandeja permanece como referencia.</p>
        </div>
      )}

      {/* Enviados / Eliminados */}
      {(tab === "sent" || tab === "deleted") && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-bold text-[#0A1C30]">{total} correo(s)</div>
              <div className="flex items-center gap-2">
                {tab === "deleted" && emails.length > 0 && (
                  <button data-testid="correos-empty-trash" onClick={emptyTrash} className="text-xs font-bold px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100">Vaciar papelera</button>
                )}
                <button onClick={() => loadBox(tab)} className="text-xs font-bold px-3 py-1.5 rounded-lg bg-black/5 text-[#0A1C30]">Actualizar</button>
              </div>
            </div>
            {loading && <div className="flex items-center gap-2 text-sm text-[#6B7688] py-6"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>}
            {!loading && emails.length === 0 && <p className="text-sm text-[#6B7688] py-6">No hay correos en esta bandeja.</p>}
            {emails.map((e) => {
              const b = STATUS_BADGE[e.status] || STATUS_BADGE.sent;
              return (
                <div key={e.id} data-testid={`correos-item-${e.id}`} onClick={() => openPreview(e.id)}
                  className={`rounded-xl border p-3 cursor-pointer transition-colors ${preview?.id === e.id ? "border-[var(--gold)] bg-[var(--gold)]/5" : "border-black/10 bg-white hover:bg-black/[0.02]"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-extrabold text-[#0A1C30] truncate">{e.subject}</div>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${b.c}`}>{b.t}</span>
                  </div>
                  <div className="text-xs text-[#6B7688] mt-0.5 truncate">Para: {e.to}</div>
                  {e.status === "failed" && e.error && (
                    <div data-testid={`correos-error-${e.id}`} className="text-[11px] text-red-600 mt-1 line-clamp-2">⚠ {e.error}</div>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[11px] text-[#8a94a6]">{e.kind} · {fmtDate(e.created_at)}</span>
                    <div className="flex gap-1" onClick={(ev) => ev.stopPropagation()}>
                      {tab === "sent" ? (
                        <button data-testid={`correos-del-${e.id}`} onClick={() => del(e.id)} title="Eliminar" className="p-1.5 rounded-lg text-[#6B7688] hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      ) : (
                        <>
                          <button data-testid={`correos-restore-${e.id}`} onClick={() => restore(e.id)} title="Restaurar" className="p-1.5 rounded-lg text-[#6B7688] hover:bg-emerald-50 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /></button>
                          <button data-testid={`correos-purge-${e.id}`} onClick={() => purge(e.id)} title="Eliminar definitivamente" className="p-1.5 rounded-lg text-[#6B7688] hover:bg-red-50 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Panel derecho: prueba + vista previa */}
          <div className="space-y-4">
            {tab === "sent" && (
              <div className="rounded-2xl border border-black/10 bg-white p-4" data-testid="correos-test">
                <div className="text-sm font-extrabold text-[#0A1C30] mb-3 flex items-center gap-2"><Send className="w-4 h-4 text-[var(--gold)]" /> Enviar correo de prueba</div>
                <div className="space-y-2">
                  <Input data-testid="correos-test-to" placeholder="correo@destino.com" value={test.to} onChange={(e) => setTest({ ...test, to: e.target.value })} />
                  <Input data-testid="correos-test-subject" placeholder="Asunto" value={test.subject} onChange={(e) => setTest({ ...test, subject: e.target.value })} />
                  <TA rows={3} placeholder="Contenido HTML" value={test.body} onChange={(e) => setTest({ ...test, body: e.target.value })} />
                  <button data-testid="correos-test-send" onClick={sendTest} disabled={sending} className="flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-xl bg-[var(--gold)] text-[#0A1C30] disabled:opacity-60">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar prueba
                  </button>
                </div>
              </div>
            )}
            {preview ? (
              <div className="rounded-2xl border border-black/10 bg-white overflow-hidden" data-testid="correos-preview">
                <div className="flex items-center justify-between px-4 py-2.5 bg-black/5">
                  <div className="text-xs font-bold text-[#6B7688]">Vista previa</div>
                  <button onClick={() => setPreview(null)} className="p-1 rounded text-[#6B7688] hover:bg-black/5"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-4 py-3 border-b border-black/5">
                  <div className="text-sm font-extrabold text-[#0A1C30]">{preview.subject}</div>
                  <div className="text-xs text-[#6B7688] mt-0.5">Para: {preview.to} · De: {preview.from}</div>
                  {preview.error && <div className="text-xs text-red-600 mt-1">Error: {preview.error}</div>}
                </div>
                <iframe title="email" srcDoc={preview.html || ""} className="w-full h-[420px] bg-white" />
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-black/15 bg-white p-10 text-center">
                <Eye className="w-8 h-8 text-[#6B7688] mx-auto mb-2" />
                <p className="text-sm text-[#6B7688]">Selecciona un correo para ver su contenido.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
